import { baseURL } from '@utils/data-fetching/utils';

/**
 * Fire-and-forget sink for browser-side Twilio Voice SDK diagnostic
 * events (`warning`, `warning-cleared`, errors, lifecycle hooks). Ships
 * events to POST /v3/recruiter-calls/webrtc-events for later analysis of
 * the WebRTC media bridge.
 *
 * Plan: bestjobs/docs/plans/2026-08-27-recruiter-call-webrtc-observability.md
 * Backend controller: RecruiterCallWebRtcEventController.
 *
 * Contract:
 * - **Fire-and-forget by design.** No `await` at any call site; a failed
 *   POST silently drops the batch. Better to lose a diagnostic than to
 *   surface a network error to a live call.
 * - Events are buffered and flushed on a short debounce so a burst of
 *   warnings collapses into a single POST.
 * - Feature-gated by env var so we can toggle on per environment without
 *   touching call sites.
 *
 * Feature flag: NEXT_PUBLIC_RECRUITER_CALL_WEBRTC_TELEMETRY_ENABLED.
 * Backend gates on its own env, so leaving this on in an environment
 * where the backend is off wastes a network round-trip but is otherwise
 * harmless. Recommended: match the two flags per environment.
 */

const FLUSH_DEBOUNCE_MS = 500;
const MAX_BATCH_SIZE = 100;
const ENDPOINT = `${baseURL}/v3/recruiter-calls/webrtc-events`;

const TELEMETRY_ENABLED = process.env.NEXT_PUBLIC_RECRUITER_CALL_WEBRTC_TELEMETRY_ENABLED === '1'
  || process.env.NEXT_PUBLIC_RECRUITER_CALL_WEBRTC_TELEMETRY_ENABLED === 'true';

interface WebRtcEventPayload {
  provider_call_sid: string;
  event_type: string;
  event_body: Record<string, unknown>;
  occurred_at: string;
}

let buffer: WebRtcEventPayload[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

const flush = (): void => {
  flushTimer = null;
  if (buffer.length === 0) return;
  const batch = buffer.slice(0, MAX_BATCH_SIZE);
  buffer = buffer.slice(MAX_BATCH_SIZE);
  // Not `await`ed. `keepalive: true` lets the request survive a tab
  // unload so a warning fired just before disconnect still reaches the
  // sink.
  fetch(ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(batch),
    keepalive: true,
  }).catch(() => {
    // Fire-and-forget by contract; drop the batch silently.
  });
  if (buffer.length > 0) {
    // A large burst was truncated to MAX_BATCH_SIZE — re-arm to send the rest.
    flushTimer = setTimeout(flush, FLUSH_DEBOUNCE_MS);
  }
};

const scheduleFlush = (): void => {
  if (flushTimer !== null) return;
  flushTimer = setTimeout(flush, FLUSH_DEBOUNCE_MS);
};

/**
 * Record a single Twilio SDK diagnostic event. Callers pass the raw
 * SDK payload verbatim; no client-side interpretation.
 *
 * `providerCallSid` may be empty when the event fires before a Call
 * object exists (e.g. Device-level warnings during mic acquisition).
 * The backend accepts empty strings and analysis correlates via
 * `user_agent` + `received_at`.
 */
export const recordWebRtcEvent = (event: {
  providerCallSid: string;
  eventType: string;
  eventBody: Record<string, unknown>;
}): void => {
  if (!TELEMETRY_ENABLED) return;
  buffer.push({
    provider_call_sid: event.providerCallSid,
    event_type: event.eventType,
    event_body: event.eventBody,
    occurred_at: new Date().toISOString(),
  });
  scheduleFlush();
};
